import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import { isStaleAssetNoise } from '@/services/staleAssetService';

const isClipboardPermissionError = (value: unknown): boolean => {
	if (!value) {
		return false;
	}

	let serializedValue = '';

	if (typeof value === 'string') {
		serializedValue = value;
	} else if (value instanceof Error) {
		serializedValue = `${value.name} ${value.message} ${value.stack ?? ''}`;
	} else if (typeof value === 'object' && value !== null && 'message' in value && typeof value.message === 'string') {
		serializedValue = value.message;
	} else {
		try {
			serializedValue = JSON.stringify(value);
		} catch {
			serializedValue = '';
		}
	}

	if (!serializedValue) {
		return false;
	}

	return serializedValue.includes("Failed to execute 'writeText' on 'Clipboard'") && serializedValue.includes('permissions policy applied to the current document');
};

const isFromRisposeSource = (value: unknown): boolean => {
	if (!value) {
		return false;
	}

	let serializedValue = '';

	if (typeof value === 'string') {
		serializedValue = value;
	} else if (value instanceof Error) {
		serializedValue = `${value.name} ${value.message} ${value.stack ?? ''}`;
	} else if (typeof value === 'object' && value !== null) {
		try {
			serializedValue = JSON.stringify(value);
		} catch {
			serializedValue = '';
		}
	}

	// The widget is served from our own domain as /rispose.js, so its frames read
	// `/rispose.js:154:64964` — they never contain the vendor's domain. Matching on
	// 'rispose.com' meant this filter never fired and the errors kept reporting.
	return /rispose/i.test(serializedValue);
};

const registerGlobalErrorFilters = () => {
	window.addEventListener('unhandledrejection', (event) => {
		if (isClipboardPermissionError(event.reason) && isFromRisposeSource(event.reason)) {
			event.preventDefault();
		}
	});

	window.addEventListener('error', (event) => {
		const isClipboardError = isClipboardPermissionError(event.error) || isClipboardPermissionError(event.message);
		const isRisposeError = isFromRisposeSource(event.error) || isFromRisposeSource(event.filename) || isFromRisposeSource(event.message);

		if (isClipboardError && isRisposeError) {
			event.preventDefault();
		}
	});
};

export const initSentry = (app: App<Element>) => {
	registerGlobalErrorFilters();

	Sentry.init({
		app,
		dsn: 'https://d818f85462e3c70fbaf3465c8a399152@o4510185212674048.ingest.de.sentry.io/4510185213919312',
		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: true,
		environment: import.meta.env.MODE || 'development',
		// Errors we can never act on from this codebase. Deliberately NOT filtered here:
		// 'Invalid video id' from the YouTube player, which means a resource has a malformed
		// youtube_id (see the backend's 2026-07-24-normalise-youtube-id migration), and
		// 'Load failed'/'Loading chunk', which are generic network failures worth seeing.
		ignoreErrors: [
			// Google Analytics internals.
			'getDuration is not a function',
			// A browser extension messaging a tab that has gone away.
			'Invalid call to runtime.sendMessage()',
			// Microsoft Outlook / Safe Links prefetching the page.
			'Object Not Found Matching Id',
		],
		denyUrls: [
			/googletagmanager\.com/i,
			/google-analytics\.com/i,
			/^chrome-extension:\/\//i,
			/^moz-extension:\/\//i,
			/^safari-(web-)?extension:\/\//i,
		],
		beforeSend(event, hint) {
			// A stale asset after a deploy is recovered automatically by staleAssetService, so
			// reporting it only produces issues nobody can act on.
			if (isStaleAssetNoise(hint.originalException) || isStaleAssetNoise(event.message)) {
				return null;
			}

			const isClipboardError = isClipboardPermissionError(hint.originalException) || isClipboardPermissionError(event.message);
			const hasRisposeFrame = event.exception?.values?.some((exception) => exception.stacktrace?.frames?.some((frame) => isFromRisposeSource(frame.filename))) ?? false;
			const isRisposeError = isFromRisposeSource(hint.originalException) || isFromRisposeSource(event.message) || isFromRisposeSource(event.request?.url) || hasRisposeFrame;

			if (isClipboardError && isRisposeError) {
				return null;
			}

			return event;
		},
		integrations: [
			Sentry.replayIntegration(),
		],
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
	});
};
